import React, { useState } from 'react'
import FormsyForm from 'formsy-react'
import { get as getConfig } from 'config'
import { connect as connectRedux } from 'react-redux'
import { EmailInput, Checkbox } from 'components/FormInput.v2'
import Button, { TYPES } from 'components/Button'
import Link from 'components/Link'
import { ICON_TYPES } from 'components/Icon.v2'
import { TARGET_BLANK } from 'components/Link/constants'
import { URL_TERMS_PRIVACY } from 'services/url/constants'
import _findIndex from 'lodash/findIndex'
import { GDPR_COUNTRIES } from 'services/country'
import { getAuthIsLoggedIn } from 'services/auth'
import Sherpa, { TYPE_SMALL } from 'components/Sherpa'

/**
 * The modal view for unknown user and expired link
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @param {Function} props.onLoginClick - The handler function for login button
 * @param {Function} props.onEmailSubmit - The handler function for email capture
 * @param {string} props.personalization - The user who shared
 * @param {boolean} props.emailProcessing - The value indicating if redeem share requestis pending
 * @param {boolean} props.isLinkExpired - The value indicating if share link is expired
 * @returns {JSX.Element} Header
 */

function SharePageV2RedeemModal (props) {
  const {
    staticText,
    personalization,
    onLoginClick,
    onEmailSubmit,
    emailProcessing,
    isLinkExpired,
    auth,
    user,
  } = props
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const { appCountry, origin } = getConfig()
  const termsUrl = `${origin}${URL_TERMS_PRIVACY}`
  const country = user.getIn(['data', 'requestCountry'], appCountry)
  const isGdprCountry = _findIndex(GDPR_COUNTRIES, (c) => c === country) > -1

  const [isOptInChecked, setIsOptInChecked] = useState(false)
  const [invalidSubmit, setInvalidSubmit] = useState(false)

  const unknownUserTitle = personalization
    ? <header className="share-page-v2__redeem-modal-header">
      {staticText.get('enjoyThisVideoFrom').replace('{name}', '')}
      <span> {personalization}</span>!
    </header>
    : <header className="share-page-v2__redeem-modal-header">
      {staticText.get('enjoyThisVideo')}
    </header>

  const expiredLinkTitle = <header className="share-page-v2__redeem-modal-header">
      {staticText.get('expiredLink')}
    </header>

  return (
    <div className="share-page-v2__redeem-modal">
      {isLinkExpired ? expiredLinkTitle : unknownUserTitle}
      <p className="share-page-v2__redeem-modal-text">
        {isLinkExpired ? staticText.get('provideNewLinkEmail') : staticText.get('provideRedeemEmail')}
      </p>
      <div className="share-page-v2__redeem-modal-content">
        <FormsyForm
          className="share-page-v2__redeem-modal-form"
          onValidSubmit={onEmailSubmit}
          onInvalidSubmit={() => { setInvalidSubmit(true) }}
          onValid={() => { setInvalidSubmit(false) }}
        >
          <EmailInput
            className="share-page-v2__redeem-modal-form-input"
            label={staticText.get('emailLabel')}
            placeholder={staticText.get('emailPlaceholder')}
            name="email"
            value=""
            validations={{ isEmail: true }}
            validationErrors={{ isEmail: staticText.get('enterValidEmail') }}
            staticText={staticText}
            required
          />
          {isGdprCountry
            ? <div className="share-page-v2__redeem-modal-form-optin-container">
              <Checkbox
                onChange={(name, checked) => {
                  setInvalidSubmit(!checked)
                  setIsOptInChecked(checked)
                }}
                name="optIn"
                label={staticText.get('optIn')}
                className="share-page-v2__redeem-modal-form-optin"
                validations={{ equals: true }}
                validationErrors={{ equals: invalidSubmit ? 'requiredField' : null }}
                note={invalidSubmit ? staticText.get('requiredField') : null}
                value={isOptInChecked}
                setValueOnKeyUp
              />
            </div>
            : null
          }
          {emailProcessing
            ? <div className="share-page-v2__loader-wrapper">
              <Sherpa type={TYPE_SMALL} />
            </div>
            : <>
              {isLinkExpired
                ? <Button
                  className="share-page-v2__redeem-modal-form-button"
                  type={TYPES.PRIMARY}
                  submit
                  disabled={emailProcessing}
                >
                  {staticText.get('getNewLink')}
                </Button>
                : <Button
                  className="share-page-v2__redeem-modal-form-button"
                  type={TYPES.ICON_PRIMARY}
                  icon={ICON_TYPES.PLAY}
                  submit
                  disabled={emailProcessing}
                >
                  {staticText.get('playVideo')}
                </Button>
              }
            </>
          }
        </FormsyForm>
        <p className="share-page-v2__redeem-modal-terms">
          {`${staticText.get('termsLabelStart')} `}
          <Link
            className="share-page-v2__redeem-modal-terms-link"
            directLink
            to={termsUrl}
            target={TARGET_BLANK}
          >
            {staticText.get('termsLabelLink')}
          </Link>
          {isGdprCountry
            ? null
            : <>
              {`, ${staticText.get('termsLabelEnd')} `}
              {`${staticText.get('termsLabelUnsubscribe')} `}
            </>
          }
        </p>
        {isLoggedIn
          ? null
          : <div className="share-page-v2__redeem-modal-signin">
            {isLinkExpired ? staticText.get('memberOfGaia') : staticText.get('alreadyAMember')}
            <Button
              type={TYPES.GHOST}
              onClick={onLoginClick}
            >
              {staticText.get('signIn')}
            </Button>
          </div>
        }
      </div>
    </div>
  )
}

export default connectRedux((state) => ({
  emailProcessing: state.share.get('emailProcessing'),
  auth: state.auth,
  user: state.user,
}))(SharePageV2RedeemModal)
